import type { SlateContent } from 'domains/reporter/Reporter/types';
import type {
  GenerateImpressionMutation,
  GenerateImpressionMutationVariables,
} from '../generated/graphql';
import type { Dispatch, SetStateAction } from 'types';
import { GENERATE_IMPRESSION } from '../modules/Apollo/queries';
import { ApolloError, useMutation } from '@apollo/client';
import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef,
} from 'react';
import { useHeadingKeywords } from './useHeadingKeywords';
import {
  Editor,
  ReactEditor,
  Transforms,
  Range,
  Node,
  Path,
  useSlate,
} from 'domains/reporter/RichTextEditor/core';
import { logger } from 'modules/logger';
import type { Descendant, Location, Point } from 'domains/reporter/RichTextEditor/core';
import { HeadingLevel } from 'domains/reporter/RichTextEditor/plugins/heading/constants';
import {
  insertHeading,
  getHeadingNodeWithSynonym,
  getHeadingSectionEdgesByNode,
} from 'domains/reporter/RichTextEditor/plugins/heading/utils/normalization';
import {
  createInlineParagraph,
  createParagraph,
} from 'domains/reporter/RichTextEditor/plugins/paragraph/utils';
import { useCurrentUser } from 'hooks/useCurrentUser';
import { useFeatureFlagEnabled, FF } from 'modules/feature-flags';
import { convertUnifiedToFields } from 'domains/reporter/Reporter/Fields/utils';
import { partialEditor, slateContentToString } from 'domains/reporter/RichTextEditor/utils';
import { useRecoilState } from 'recoil';
import {
  prevGeneratedImpressionState,
  prevReportSectionsState,
} from 'domains/reporter/Reporter/state';
import { useToasterDispatch } from '../common/ui/Toaster/Toaster';
import { useReportLockedState } from 'domains/reporter/Reporter/hooks/useReportLockedState';
import scrollIntoViewIfNeeded from 'scroll-into-view-if-needed';
import equals from 'lodash/fp/equals';
import { slateSource } from 'domains/reporter/RichTextEditor/core/slateSource';
import { annotateTextWithSource } from 'domains/reporter/RichTextEditor/plugins/textSourceStyling/utils';
import { Element, Text } from 'slate';
import { useCurrentCaseContext } from 'common/CurrentCase/CurrentCaseProvider';
import { telemetryContextFor } from 'modules/Apollo/types';
import { BRACKET_TYPES } from 'domains/reporter/RichTextEditor/constants';
import { useIsAutoImpressionAllowedForModality } from './useIsAutoImpressionAllowedForModality';
import {
  HEADING_PLUGIN_ID,
  INLINE_BOOKMARK_PLUGIN_ID,
  LIST_PLUGIN_ID,
  PARAGRAPH_LIST_ITEM_VARIANT,
  PARAGRAPH_PLUGIN_ID,
} from 'domains/reporter/RichTextEditor';
import { convertParagraphsIntoLineBreaks } from 'domains/reporter/RichTextEditor/plugins/lineBreak/utils';
import { stripCarriageReturnsFromContent } from 'domains/reporter/RichTextEditor/utils/stripCarriageReturnsFromContent';
import {
  AutoImpressionRenderMetadata,
  measureAutoImpressionRenderEnd,
  measureAutoImpressionRenderStart,
} from 'domains/reporter/RichTextEditor/analytics/performance';
import { useSendModifyDictationMessage } from 'common/Recorder/useRecorder';

export type ImpressionContent = {
  content: Nullable<SlateContent>;
  range: Nullable<Range>;
  rangeWithHeading: Nullable<Range>;
};

export type GenerateImpressionState = 'initial' | 'loading' | 'visited' | 'stale' | 'error';

const IMPRESSION_GENERATION_FAILED_MESSAGE =
  'Impression generation failed. Please try again later.';

const getErrorMessage = (error: unknown): string => {
  if (error instanceof Error) return error.message;
  if (typeof error === 'string') return error;
  return '';
};

const getGenerateImpressionFailureMetadata = ({
  error,
  durationMs,
  caseSmid,
  state,
}: {
  error: unknown;
  durationMs: number;
  caseSmid: Nullable<string>;
  state: GenerateImpressionState;
}) => {
  const apolloError = error instanceof ApolloError ? error : null;
  const networkError = apolloError?.networkError;

  return {
    operationName: 'generateImpression',
    worklistID: caseSmid,
    durationMs,
    statusCode:
      networkError != null &&
      'statusCode' in networkError &&
      typeof networkError.statusCode === 'number'
        ? networkError.statusCode
        : null,
    errorMessage: getErrorMessage(error),
    errorCode:
      networkError != null && 'code' in networkError && typeof networkError.code === 'string'
        ? networkError.code
        : null,
    graphQLErrorCodes:
      apolloError?.graphQLErrors
        .map((graphQLError) => graphQLError.extensions?.code)
        .filter((code): code is string => typeof code === 'string') ?? [],
    state,
  };
};

export type UseImpressionGeneratorReturn = {
  /**
   * Determines whether the user's selection is entirely within the impression section.
   * @param {Editor} editor - The Slate editor instance.
   * @param {?RangeType} selection - The selection to check. If null, the current selection is used.
   * @returns {boolean} True if selection entirely within the impression section.
   */
  isSelectionInImpressionSection: (editor: Editor, selection?: Range) => boolean;
  /**
   * Retrieves the content and range of the impression section in the editor.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {?ImpressionContent} The content and range of the impression section, or null if not found.
   */
  getImpressionContent: (editor: Editor) => Nullable<ImpressionContent>;
  /**
   * Retrieves the content of all sections of the report except the impression section.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {SlateContent} The content excluding the impression section.
   */
  getNonImpressionContent: (editor: Editor) => SlateContent;
  /**
   * Generates an impression for the current case and returns the content of the impression.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {Promise<?SlateContent>} The generated impression content, or null if generation fails.
   */
  generateImpression: (editor: Editor) => Promise<Nullable<SlateContent>>;
  /**
   * Stitches the given impression content into the editor. If there is no existing impression section,
   * it first creates an impression heading at the end of the report. If there is one, then the existing content
   * is removed and replaced with the new content. Returns the impression and its range.
   * @param {Editor} editor - The Slate editor instance.
   * @param {SlateContent} content - The impression content to be stitched into the editor.
   * @param {SlateContent} isAutoGenerated - Whether the impression is auto-generated. Defaults to false.
   * @returns {?ImpressionContent} The stitched impression content and its range, or null if stitching fails.
   */
  stitchImpression: (
    editor: Editor,
    content: SlateContent,
    isAutoGenerated?: boolean
  ) => Nullable<ImpressionContent>;
  /**
   * Generates an impression and stitches it into the editor. Returns the impression and its range.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {Promise<?ImpressionContent>} The stitched impression content and its range, or null if generation or stitching fails.
   */
  generateAndStitchImpression: (
    editor: Editor,
    isAutoGenerated?: boolean,
    autoImpressionRenderMetadata?: AutoImpressionRenderMetadata
  ) => Promise<Nullable<ImpressionContent>>;
  /**
   * Determines if the impression in the report is generated by comparing it to the most recently generated impression.
   * @param {Editor} editor - The Slate editor instance.
   * @returns {boolean} Whether the current impression in the report is generated
   */
  isCurrentImpressionGenerated: (editor: Editor) => boolean;
  /**
   * The state of the impression generation button determined by the generateImpression function call.
   */
  generateImpressionState: GenerateImpressionState;
  /**
   * If impression generator is disabled for the case or user settings
   */
  impressionGeneratorNotAllowed: boolean;
  /**
   * Resets the impression generation state to 'initial' when new template is loaded
   */
  resetGenerateImpressionState: () => void;
  /**
   * Determines if the given impression content is empty.
   * @param {SlateContent} content - The impression content to check.
   * @returns {boolean} True if the content is empty.
   */
  isImpressionContentEmpty: (content: SlateContent) => boolean;
  /**
   * Determines if the user has navigated to the impression section for the first time
   */
  isFirstNavigationToImpressionSection: boolean;
  /**
   * Sets the flag to determine if the user has navigated to the impression section for the first time
   * @param {boolean} isFirstNavigationToImpressionSection - The flag to set
   * @returns {void}
   */
  setIsFirstNavigationToImpressionSection: Dispatch<SetStateAction<boolean>>;
};

export const ImpressionGeneratorContext: React.Context<Nullable<UseImpressionGeneratorReturn>> =
  createContext<Nullable<UseImpressionGeneratorReturn>>(undefined);

const ImpressionGeneratorProviderInner = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null => {
  const headingKeywords = useHeadingKeywords({ context: APOLLO_QUERY_CONTEXT });
  const { data } = useCurrentUser({ context: APOLLO_QUERY_CONTEXT });
  const impressionGeneratorSettings = data?.me?.reporterSettings?.impressionGenerator;
  const [isAutoImpressionsEnabled] = useFeatureFlagEnabled(FF.REPORTER_AUTO_IMPRESSIONS);
  const isImpressionModalityAllowed = useIsAutoImpressionAllowedForModality({
    context: APOLLO_QUERY_CONTEXT,
  });
  const { currentCase } = useCurrentCaseContext();
  const { enqueueToast } = useToasterDispatch();
  const { isAddendumStatus } = useReportLockedState({ context: APOLLO_QUERY_CONTEXT });
  const [generateImpressionState, setGenerateImpressionState] =
    useState<GenerateImpressionState>('initial');
  const previousReportContent = useRef<Nullable<string>>(null);
  const generationReportSections = useRef<ReturnType<typeof convertUnifiedToFields> | null>(null);
  const hasRetriedStaleImpression = useRef(false);
  const shouldAutoRetryStaleImpression = useRef(false);
  const isGenerateImpressionRequestInFlight = useRef(false);
  const generationEpoch = useRef(0);
  const sendModifyDictationMessage = useSendModifyDictationMessage();
  const [isFirstNavigationToImpressionSection, setIsFirstNavigationToImpressionSection] =
    useState(true);

  const [prevGeneratedImpression, setPrevGeneratedImpression] = useRecoilState(
    prevGeneratedImpressionState
  );
  const [prevReportSections, setPrevReportSections] = useRecoilState(prevReportSectionsState);
  const [generateImpression, { loading }] = useMutation<
    GenerateImpressionMutation,
    GenerateImpressionMutationVariables
  >(GENERATE_IMPRESSION, {
    context: APOLLO_QUERY_CONTEXT,
  });
  const editor = useSlate();

  const impressionGeneratorNotAllowed =
    isAddendumStatus || !isAutoImpressionsEnabled || !impressionGeneratorSettings?.enabled;

  useEffect(() => {
    if (loading) {
      setGenerateImpressionState('loading');
    }
  }, [loading]);

  const getAnyImpressionHeadingNode = useCallback(
    (editor: Editor) => {
      return (
        getHeadingNodeWithSynonym(editor, headingKeywords.impressions, HeadingLevel.H1) ??
        getHeadingNodeWithSynonym(editor, headingKeywords.impressions, HeadingLevel.H2)
      );
    },
    [headingKeywords.impressions]
  );

  const getImpressionContent = useCallback(
    (editor: Editor): Nullable<ImpressionContent> => {
      const impressionHeadingNode = getAnyImpressionHeadingNode(editor);

      if (impressionHeadingNode == null) {
        return null;
      }

      const headingSectionRange = getHeadingSectionEdgesByNode(editor, impressionHeadingNode);
      if (headingSectionRange == null) {
        return null;
      }

      return {
        content: Editor.fragment(editor, {
          anchor: Editor.start(editor, impressionHeadingNode[1]),
          focus: headingSectionRange.focus,
        }),
        range: headingSectionRange,
        rangeWithHeading: {
          anchor: Editor.start(editor, impressionHeadingNode[1]),
          focus: headingSectionRange.focus,
        },
      };
    },
    [getAnyImpressionHeadingNode]
  );

  const getNonImpressionContent = useCallback(
    (editor: Editor): SlateContent => {
      const impressionHeadingNode = getAnyImpressionHeadingNode(editor);

      if (impressionHeadingNode == null) {
        return editor.children;
      }

      const headingSectionRange = getHeadingSectionEdgesByNode(editor, impressionHeadingNode);
      if (headingSectionRange == null) {
        return editor.children;
      }

      let preImpressionContent: Array<Descendant> = [];
      let postImpressionContent: Array<Descendant> = [];

      const pointBefore = Editor.before(editor, impressionHeadingNode[1]);
      if (pointBefore != null) {
        preImpressionContent = Editor.fragment(editor, {
          anchor: Editor.start(editor, []),
          focus: pointBefore,
        });
      }

      const pointAfter = Editor.after(editor, headingSectionRange.focus);
      if (pointAfter != null) {
        postImpressionContent = Editor.fragment(editor, {
          anchor: pointAfter,
          focus: Editor.end(editor, []),
        });
      }

      return preImpressionContent.concat(postImpressionContent);
    },
    [getAnyImpressionHeadingNode]
  );

  const getNormalizedReportSections = useCallback(
    (editor: Editor, nonImpressionContent: SlateContent) => {
      let reportSections = convertUnifiedToFields(nonImpressionContent);
      const nonImpressionText = slateContentToString(nonImpressionContent).trim();
      const hasReportText = slateContentToString(reportSections).trim().length > 0;

      // Defensive fallback for malformed section conversion so generation and stale checks
      // use the same normalized representation.
      if (!hasReportText && nonImpressionText.length > 0) {
        logger.warn(
          `[useImpressionsGenerator] Falling back to plain-text report sections because converted sections were empty`,
          {
            worklistID: currentCase?.smid,
            convertedReportSections: reportSections,
            nonImpressionTextLength: nonImpressionText.length,
            editor: partialEditor(editor),
          }
        );
        reportSections = [
          {
            type: 'field',
            label: '',
            children: [createParagraph(nonImpressionText)],
            isCompleted: true,
          },
        ];
      }

      return reportSections;
    },
    [currentCase?.smid]
  );

  const generateImpressionInternal = useCallback(
    async (editor: Editor): Promise<Nullable<SlateContent>> => {
      try {
        const impressionHeadingNode = getAnyImpressionHeadingNode(editor);
        if (impressionHeadingNode) {
          const target = ReactEditor.toDOMNode(editor, impressionHeadingNode[0]);
          scrollIntoViewIfNeeded(target, {
            behavior: 'smooth',
            scrollMode: 'if-needed',
            block: 'center',
          });
        }
      } catch (e: any) {
        // no-op, this can happen if we run Slate headlessly since there will be no
        // associated DOM
      }

      if (isAddendumStatus) {
        logger.warn(
          `[useImpressionsGenerator] Impression generation is not available for addendums`
        );
        enqueueToast('Impression generation is not available for addendums', { severity: 'error' });
        return null;
      }

      const currentCaseID = currentCase?.smid;

      if (!isAutoImpressionsEnabled) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not available for this account`
        );
        return null;
      }

      if (impressionGeneratorSettings?.enabled !== true) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not enabled`
        );
        return null;
      }

      const nonImpressionContent = getNonImpressionContent(editor);
      const reportSections = getNormalizedReportSections(editor, nonImpressionContent);
      generationReportSections.current = reportSections;
      setPrevReportSections(reportSections);

      if (currentCaseID == null || reportSections.length === 0) {
        logger.error(
          `[useImpressionsGenerator] Impression could not be generated because caseSmid or reportSections is null`,
          {
            worklistID: currentCaseID,
            reportSections,
            editor: partialEditor(editor),
          }
        );
        setGenerateImpressionState('error');
        return null;
      }

      const requestStartTime = performance.now();

      try {
        const impressionData = await generateImpression({
          variables: {
            input: { caseSmid: currentCaseID, reportSections },
          },
        });

        logger.info(`[useImpressionsGenerator] Impression generated for case ${currentCaseID}`, {
          content: reportSections,
          impression: impressionData,
          worklistID: currentCaseID,
          editor: partialEditor(editor),
          state: generateImpressionState,
        });

        const impressionContent = annotateTextWithSource(
          impressionData?.data?.generateImpression?.impression,
          {
            source: slateSource.ai.impressionGenerator,
          }
        );
        return stripCarriageReturnsFromContent(impressionContent);
      } catch (e: unknown) {
        const failureMetadata = getGenerateImpressionFailureMetadata({
          error: e,
          durationMs: Math.round(performance.now() - requestStartTime),
          caseSmid: currentCaseID,
          state: generateImpressionState,
        });
        logger.error('[useImpressionsGenerator] Error generating impression', e, {
          ...failureMetadata,
          reportSections: reportSections,
          editor: partialEditor(editor),
        });
        setGenerateImpressionState('error');
        throw e;
      }
    },
    [
      currentCase?.smid,
      enqueueToast,
      generateImpression,
      generateImpressionState,
      getAnyImpressionHeadingNode,
      getNonImpressionContent,
      getNormalizedReportSections,
      impressionGeneratorSettings?.enabled,
      isAddendumStatus,
      isAutoImpressionsEnabled,
      setPrevReportSections,
    ]
  );

  const getInsertionPoint = useCallback(
    (editor: Editor): Point => {
      // Use the H1-preferring lookup (H1, then H2) rather than a level-agnostic first match.
      // A level-agnostic match returns the first impression-synonym heading in document order,
      // which can be a "Recommendation:" H2 nested inside an earlier Findings section, causing the
      // generated impression to be stitched into Findings instead of the trailing IMPRESSION H1.
      const impressionHeadingEntry = getAnyImpressionHeadingNode(editor);

      // If there is no impression heading, we will need to insert a heading and its necessary
      // inline paragraph and paragraph nodes
      if (!impressionHeadingEntry) {
        logger.info(
          `[useImpressionsGenerator] Inserting impression heading because it does not exist`,
          {
            content: editor.children,
            state: generateImpressionState,
          }
        );

        Transforms.insertNodes(
          editor,
          [
            insertHeading(HeadingLevel.H1, 'Impression:'),
            createInlineParagraph(),
            createParagraph(),
          ],
          { at: Editor.end(editor, []) }
        );
        return Editor.end(editor, []);
      }

      // An impression section exists.
      const [, impressionHeadingPath] = impressionHeadingEntry;

      const nextHeadingEntry = Editor.next(editor, {
        at: impressionHeadingPath,
        match: (n: Node) => n.type === HEADING_PLUGIN_ID && n.level === HeadingLevel.H1,
      });

      const headingEdges = {
        anchor: Editor.after(editor, impressionHeadingPath) || Editor.end(editor, []),
        focus:
          (nextHeadingEntry && Editor.before(editor, nextHeadingEntry[1])) ||
          Editor.end(editor, []),
      };

      // Find a range over existing content that should be replaced by the new impression content.
      // Account for in-line, compact, or spaced formatting of section content,
      // while preserving non-heading content after the impression section.

      // The start of content here means the first bracket node or non-whitespace text, whichever comes first.
      // If there is no text and no bracket, place the impression on the second empty line, or at the end of the section.

      // local helpers
      const isBracket = (n: Node) =>
        Element.isElement(n) && BRACKET_TYPES.some((t) => n.type === t);
      const isNotBlankText = (n: Node) => Text.isText(n) && n.text.trim().length !== 0;
      const isListMarker = (n: Node): boolean => {
        const s = Node.string(n).trimStart();
        return (
          // ordered markers: 1. 2)
          // unordered markers: -, *
          /^[-*]\s*/.test(s) || /^\d+[.)]\s*/.test(s)
        );
      };
      const replaceList = (path: Path) => {
        Editor.withoutNormalizing(editor, () => {
          Transforms.removeNodes(editor, { at: path });
          Transforms.insertNodes(editor, createParagraph(), { at: path });
        });
      };

      // Get all the level-one nodes in the section.
      const firstLevelEntries = Array.from(
        Editor.nodes(editor, {
          at: headingEdges,
          mode: 'highest',
          match: (n) => Element.isElement(n),
        })
      );
      let startPoint = null,
        endPoint = null,
        i = 0,
        lastEmptyLineIndex = null;

      for (const [node, path] of firstLevelEntries) {
        const firstBracketOrText = Editor.nodes(editor, {
          at: path,
          match: (n) => isBracket(n) || isNotBlankText(n),
        }).next().value;

        // If the start point resides inside the list block, the list will not be deleted.
        // Instead, replace the list right away with a normal paragraph.
        const isListNode = node.type === LIST_PLUGIN_ID;
        if (isListNode) replaceList(path);

        if (!startPoint) {
          // Only search up to the second blank line after the heading.
          if (i === 3) {
            // If we've reached the 4th top-level entry (inline paragraph + two previous block entries)
            // without finding a start point, then we set it to the start of the *second* line after the heading.
            startPoint = Editor.start(editor, firstLevelEntries[i - 1][1]);
          } else if (isListNode) {
            startPoint = Editor.end(editor, path);
          } else {
            // Look for a bracket or non-blank text node in the section.
            if (firstBracketOrText) {
              const [node, path] = firstBracketOrText;
              if (Text.isText(node)) {
                const firstCharOffset = node.text.length - node.text.trimLeft().length;
                startPoint = { path, offset: firstCharOffset };
              } else {
                // This is a bracket, so to delete it, we can set the start point before it.
                startPoint = Editor.before(editor, path);
              }
            }
          }
        }
        if (startPoint) {
          if (node.type === HEADING_PLUGIN_ID) {
            endPoint = Editor.before(editor, path) || Editor.start(editor, path);
            break;
          }

          if (!firstBracketOrText) {
            // This is effectively a blank line that is possibly a spacing line in list-like content.
            lastEmptyLineIndex = i;
          }

          if (isListMarker(node) || isListNode || lastEmptyLineIndex === null) {
            endPoint = Editor.end(editor, path);
          }
        }
        i += 1;
      }
      if (!startPoint) {
        startPoint = Editor.end(editor, headingEdges);
      }
      if (!endPoint) {
        endPoint = { ...startPoint };
      }
      const range = { anchor: startPoint, focus: endPoint };
      if (Range.isExpanded(range)) Transforms.delete(editor, { at: range });
      return startPoint;
    },
    [getAnyImpressionHeadingNode, generateImpressionState]
  );

  const stitchImpression = useCallback(
    (
      editor: Editor,
      content: SlateContent,
      isAutoGenerated = false
    ): Nullable<ImpressionContent> => {
      const sanitizedContent = stripCarriageReturnsFromContent(content);
      if (
        previousReportContent.current != null &&
        previousReportContent.current !== slateContentToString(editor.children) &&
        isAutoGenerated === true
      ) {
        const nonImpressionContent = getNonImpressionContent(editor);
        const reportSections = getNormalizedReportSections(editor, nonImpressionContent);
        // Only source-report edits should arm a retry; impression-field edits should just discard.
        shouldAutoRetryStaleImpression.current = !equals(
          generationReportSections.current,
          reportSections
        );
        logger.info(
          '[useImpressionsGenerator] Generated impression has been discarded due to the user continuing to edit the report',
          {
            state: generateImpressionState,
            previousContent: previousReportContent,
            content: editor.children,
            isAutoGenerated,
          }
        );
        return null;
      }

      // If there is no list, or just a single item, we will insert the prose in-line

      // First, get an array of just the list item nodes
      const allElements = sanitizedContent.flatMap((slateNode: Node) =>
        Array.from(Node.elements(slateNode)).map(([node]) => node)
      );
      const listItemsInImpression = allElements.reduce((a: Element[], e: Element) => {
        if (e.type === PARAGRAPH_PLUGIN_ID && e.variant === PARAGRAPH_LIST_ITEM_VARIANT) {
          a.push(e);
        }
        return a;
      }, []);

      let contentToStitch: Node[];
      let isInsertingList = true;
      // If there is no list, insert all the content in-line
      if (listItemsInImpression.length === 0) {
        isInsertingList = false;
        contentToStitch = sanitizedContent;
        // If there is a single list item, insert just the children in-line
      } else if (listItemsInImpression.length === 1) {
        isInsertingList = false;
        contentToStitch = listItemsInImpression[0].children;
        // If there are multiple list items, insert the list as a block
      } else {
        contentToStitch = sanitizedContent;
      }
      const originalSelection = editor.selection;
      let insertionPoint: Location = getInsertionPoint(editor);

      try {
        // Get the top-level parent node for the insertion point.
        const [topLevelNode, topLevelPath] = Editor.node(editor, insertionPoint.path.slice(0, 1));

        // Determine if it's the inline paragraph.
        const isInlineParagraph =
          Element.isElement(topLevelNode) &&
          topLevelNode.type === PARAGRAPH_PLUGIN_ID &&
          topLevelNode.shouldForceInline === true;

        if (isInsertingList) {
          // If the start point of the insertion range is in the inline paragraph,
          // ensure we put the list at the next top-level path.
          if (isInlineParagraph) {
            insertionPoint = Path.next(topLevelPath);
            // If the start point is further down, replace the given top-level node with the list.
          } else {
            insertionPoint = topLevelPath;
            Transforms.removeNodes(editor, { at: topLevelPath });
          }
          contentToStitch = wrapListParagraphs(contentToStitch);
        } else {
          contentToStitch = [
            {
              type: INLINE_BOOKMARK_PLUGIN_ID,
              children: convertParagraphsIntoLineBreaks(contentToStitch),
            },
          ];
          // Insert this bookmark at the insertion point when in an inline paragraph, otherwise offset does not matter.
          insertionPoint = isInlineParagraph ? insertionPoint : insertionPoint.path;
        }
        Transforms.insertNodes(editor, contentToStitch, { at: insertionPoint });

        logger.info(
          `[useImpressionsGenerator] Generated impression has been stitched into editor`,
          {
            content: editor.children,
            impression: sanitizedContent,
            state: generateImpressionState,
          }
        );

        const impressionContent = getImpressionContent(editor);
        if (impressionContent == null) {
          return null;
        }

        if (impressionContent.range != null) {
          // Select the contents of the first bracket node, if it exists, otherwise select the end of the impression content
          const firstBracketEntry = Editor.nodes(editor, {
            at: impressionContent.range,
            match: (n) =>
              Element.isElement(n) &&
              BRACKET_TYPES.some((t) => t === n.type) &&
              Array.from(Node.texts(n)).some(
                ([text]) => text.source === slateSource.ai.impressionGenerator
              ),
          }).next().value;
          if (firstBracketEntry) {
            Transforms.select(editor, firstBracketEntry[1]);
          } else {
            // If there is no inline bookmark, select the end of the impression content
            Transforms.select(editor, Editor.end(editor, impressionContent.range));
          }
        }

        return impressionContent;
      } catch (error) {
        logger.warn(`[useImpressionsGenerator] Impression could not be stitched due to an error.`, {
          error: error,
          content: editor.children,
          impression: content,
          state: generateImpressionState,
        });

        if (originalSelection != null) {
          Transforms.select(editor, originalSelection);
        }

        enqueueToast(IMPRESSION_GENERATION_FAILED_MESSAGE, {
          severity: 'error',
        });

        return null;
      }
    },
    [
      enqueueToast,
      generateImpressionState,
      getImpressionContent,
      getInsertionPoint,
      getNonImpressionContent,
      getNormalizedReportSections,
    ]
  );

  const generateAndStitchImpression = useCallback(
    async (
      editor: Editor,
      isAutoGenerated = false,
      autoImpressionRenderMetadata?: AutoImpressionRenderMetadata
    ): Promise<Nullable<ImpressionContent>> => {
      if (loading || isGenerateImpressionRequestInFlight.current) {
        logger.info(
          `[useImpressionsGenerator] Impression generation request ignored because another request is already in flight`
        );
        return null;
      }
      shouldAutoRetryStaleImpression.current = false;

      if (isAddendumStatus) {
        logger.warn(
          `[useImpressionsGenerator] Impression generation is not available for addendums`
        );
        enqueueToast('Impression generation is not available for addendums', { severity: 'error' });
        return null;
      }

      if (!isAutoImpressionsEnabled) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not available for this account`
        );
        return null;
      }

      if (impressionGeneratorSettings?.enabled !== true) {
        logger.warn(
          `[useImpressionsGenerator] Impression could not be generated because impression generation is not enabled`
        );
        return null;
      }

      if (isAutoGenerated === true) {
        if (!isImpressionModalityAllowed) {
          logger.warn(
            `[useImpressionsGenerator] Impression could not be automatically generated because impression generation is not enabled for ${currentCase?.modality ?? 'null'} modality`,
            {
              modality: currentCase?.modality,
              enabledModalities: impressionGeneratorSettings?.modalities,
              case: currentCase?.smid,
            }
          );
          return null;
        }

        previousReportContent.current = slateContentToString(editor.children);
      } else {
        previousReportContent.current = null;
      }

      if (generateImpressionState === 'visited') {
        logger.info(
          `[useImpressionsGenerator] Replacing existing generated impression without changes to the report`,
          {
            content: editor.children,
            state: generateImpressionState,
            impression: prevGeneratedImpression,
          }
        );
      } else if (generateImpressionState === 'stale') {
        logger.info(
          `[useImpressionsGenerator] Replacing existing generated impression after changes to the report`,
          {
            content: editor.children,
            state: generateImpressionState,
            impression: prevGeneratedImpression,
          }
        );
      } else if (generateImpressionState === 'initial') {
        logger.info(`[useImpressionsGenerator] Generating impression for the first time`, {
          content: editor.children,
          state: generateImpressionState,
        });
      }

      if (autoImpressionRenderMetadata != null) {
        measureAutoImpressionRenderStart();
      }

      isGenerateImpressionRequestInFlight.current = true;
      const requestGenerationEpoch = generationEpoch.current;
      let stitchedImpression = null;
      try {
        const impression = await generateImpressionInternal(editor);
        if (impression == null) {
          return null;
        }

        // Drop responses invalidated by template/report state reset before stitching.
        if (requestGenerationEpoch !== generationEpoch.current) {
          logger.info(
            '[useImpressionsGenerator] Generated impression has been discarded because generation state was reset before the request completed',
            {
              state: generateImpressionState,
              content: editor.children,
              isAutoGenerated,
            }
          );
          setGenerateImpressionState('initial');
          return null;
        }

        stitchedImpression = stitchImpression(editor, impression, isAutoGenerated);
        if (stitchedImpression != null && stitchedImpression.content != null) {
          setPrevGeneratedImpression(slateContentToString(stitchedImpression.content));
          sendModifyDictationMessage();
        }
        setGenerateImpressionState('visited');
        if (stitchedImpression != null && autoImpressionRenderMetadata != null) {
          measureAutoImpressionRenderEnd(autoImpressionRenderMetadata);
        }
      } catch {
        enqueueToast(IMPRESSION_GENERATION_FAILED_MESSAGE, {
          severity: 'error',
        });
      } finally {
        isGenerateImpressionRequestInFlight.current = false;
      }

      return stitchedImpression;
    },
    [
      isAddendumStatus,
      isAutoImpressionsEnabled,
      isImpressionModalityAllowed,
      impressionGeneratorSettings?.enabled,
      impressionGeneratorSettings?.modalities,
      generateImpressionState,
      enqueueToast,
      currentCase?.modality,
      currentCase?.smid,
      prevGeneratedImpression,
      loading,
      generateImpressionInternal,
      stitchImpression,
      setPrevGeneratedImpression,
      sendModifyDictationMessage,
    ]
  );

  useEffect(() => {
    if (generateImpressionState !== 'visited' || editor == null || editor.children == null) {
      return;
    }
    const nonImpressionContent = getNonImpressionContent(editor);
    const reportSections = getNormalizedReportSections(editor, nonImpressionContent);

    if (equals(prevReportSections, reportSections)) return;

    logger.warn(
      `[useImpressionsGenerator] Report content has been edited and generated impression is now stale`,
      {
        content: reportSections,
      }
    );

    setGenerateImpressionState('stale');

    // Only auto-retry for stale state caused by edits while auto-generation was in flight.
    if (!shouldAutoRetryStaleImpression.current) return;

    // Prevent duplicate auto-retries once a stale impression has already been retried.
    if (hasRetriedStaleImpression.current) return;
    // Prevent concurrent auto-retry while a generation mutation is already in flight.
    if (loading) return;

    hasRetriedStaleImpression.current = true;
    shouldAutoRetryStaleImpression.current = false;
    logger.info(
      `[useImpressionsGenerator] Auto re-triggering impression generation after stale state`
    );

    generateAndStitchImpression(editor, true);
  }, [
    editor,
    editor?.children,
    generateImpressionState,
    getNonImpressionContent,
    getNormalizedReportSections,
    loading,
    generateAndStitchImpression,
    prevReportSections,
  ]);

  /**
   * Returns whether the current impression has been generated in the editor by comparing the
   * content of the most recently generated impression with the content of the current impression
   */
  const isCurrentImpressionGenerated = useCallback(
    (editor: Editor): boolean => {
      if (prevGeneratedImpression == null) {
        return false;
      }

      const impressionContent = getImpressionContent(editor);
      if (impressionContent == null || impressionContent.content == null) {
        return false;
      }

      return slateContentToString(impressionContent.content) === prevGeneratedImpression;
    },
    [getImpressionContent, prevGeneratedImpression]
  );

  const resetGenerateImpressionState = useCallback(() => {
    hasRetriedStaleImpression.current = false;
    shouldAutoRetryStaleImpression.current = false;
    generationEpoch.current += 1;
    setGenerateImpressionState('initial');
  }, [setGenerateImpressionState]);

  const isSelectionInImpressionSection = useCallback(
    (editor: Editor, newSelection?: Range): boolean => {
      const impressionRange = getImpressionContent(editor)?.rangeWithHeading;
      const selection = newSelection ?? editor.selection;

      return (
        impressionRange != null &&
        selection != null &&
        Range.includes(impressionRange, selection.anchor) &&
        Range.includes(impressionRange, selection.focus)
      );
    },
    [getImpressionContent]
  );

  const isImpressionContentEmpty = useCallback(
    (impressionContent: ImpressionContent | null): boolean => {
      const content = impressionContent?.content;
      // This really implies that it is missing - not that it is empty
      if (!content || content.length === 0 || headingKeywords.impressions == null) {
        return false;
      }
      const lowerKeywords = headingKeywords.impressions.map((keyword) => keyword.toLowerCase());
      const transformedHeading = content[0].children[0].text.trim().toLowerCase().replace(':', '');
      if (content[0].type === 'heading' && !lowerKeywords.includes(transformedHeading)) {
        return false;
      }

      const isEmptyTextNode = (node: Node): boolean => {
        if (Text.isText(node)) {
          return node.text.trim() === '';
        }
        if (Element.isElement(node)) {
          return node.children.every(isEmptyTextNode);
        }
        return true;
      };
      const isContentEmpty = content.slice(1).every((node: Node) => isEmptyTextNode(node));

      return isContentEmpty;
    },
    [headingKeywords.impressions]
  );

  const impressionGeneratorBag = useMemo(
    () => ({
      isSelectionInImpressionSection,
      getImpressionContent,
      getNonImpressionContent,
      generateImpression: generateImpressionInternal,
      stitchImpression,
      generateAndStitchImpression,
      isCurrentImpressionGenerated,
      generateImpressionState,
      impressionGeneratorNotAllowed,
      resetGenerateImpressionState,
      isImpressionContentEmpty,
      isFirstNavigationToImpressionSection,
      setIsFirstNavigationToImpressionSection,
    }),
    [
      isSelectionInImpressionSection,
      getImpressionContent,
      getNonImpressionContent,
      generateImpressionInternal,
      stitchImpression,
      generateAndStitchImpression,
      isCurrentImpressionGenerated,
      generateImpressionState,
      impressionGeneratorNotAllowed,
      resetGenerateImpressionState,
      isImpressionContentEmpty,
      isFirstNavigationToImpressionSection,
    ]
  );

  return (
    <ImpressionGeneratorContext.Provider value={impressionGeneratorBag}>
      {children}
    </ImpressionGeneratorContext.Provider>
  );
};

/**
 * This context provider is a wrapper that acts as a pass-through if there
 * exists an ancestor component that is an ImpressionGeneratorContext.Provider,
 * otherwise it provides meaningful implementation from
 * ImpressionGeneratorProviderInner.
 *
 * This is a workaround for the fact that this context is nested-- an editor
 * plugin creates a context, then the editor itself uses it lower in the tree.
 **/
export const ImpressionGeneratorProvider = ({
  children,
}: {
  children: React.ReactNode;
}): React.ReactElement | null => {
  const parentProvider = useContext(ImpressionGeneratorContext);
  if (parentProvider) {
    // Avoid shadowing an existing provider; nested providers should be a no-op.
    return <>{children}</>;
  }

  return <ImpressionGeneratorProviderInner>{children}</ImpressionGeneratorProviderInner>;
};

/**
 * Custom hook to generate and manage impression content within a Slate editor.
 * This hook provides functionality to retrieve impression content, generate new impressions,
 * and stitch them into the editor.
 *
 * @returns {UseImpressionGeneratorReturn} An object containing methods to:
 *  - getImpressionContent: Retrieves the impression content and its range from the editor.
 *  - getNonImpressionContent: Retrieves the content of the editor excluding the impression section.
 *  - generateImpression: Generates an impression for the current case and returns the impression content.
 *  - stitchImpression: Stitches given impression content into the editor.
 *  - generateAndStitchImpression: Generates an impression and stitches it into the editor.
 *
 **/
export const useImpressionGenerator = (): UseImpressionGeneratorReturn => {
  const impressionGenerator = useContext(ImpressionGeneratorContext);

  if (!impressionGenerator) {
    throw new Error(
      '[useImpressionGenerator] Hook must be used within a ImpressionGeneratorProvider.'
    );
  }

  return impressionGenerator;
};

// Helper function to wrap list items in inlineBookmarks in the impression content.
export function wrapListParagraphs(nodes: Node[]) {
  return nodes.map((node: Node): Node => {
    if (!Element.isElement(node)) {
      // text nodes pass through unchanged
      return node;
    }
    if (node.type === 'paragraph' && node.variant === 'li') {
      return {
        ...node,
        // wrap all original children in an inlineBookmark
        children: [
          {
            type: 'inlineBookmark',
            // carry over any additional element-level props if needed
            // e.g. id, data-attrs, etc.
            children: node.children,
          },
        ],
      };
    }
    // otherwise recurse into its children
    return {
      ...node,
      children: wrapListParagraphs(node.children),
    };
  });
}

ImpressionGeneratorProvider.displayName = 'ImpressionGeneratorProvider';

const APOLLO_QUERY_CONTEXT = telemetryContextFor(ImpressionGeneratorProvider);
